import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
//import Carousel from 'react-material-ui-carousel'

import { Icons, Loader } from '@clientbase/clientbase-library'
import { faRocket } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, useMediaQuery } from '@mui/material'
import * as Sentry from '@sentry/nextjs'
import { DataService } from 'api/DataService'
import { useAuthContext } from 'context/AuthContext'
import { usePermissionsContext } from 'context/PermissionsContext'
import AccountAlertBanner from 'features/AccountAlerts/components/AccountAlertBanner'
import { useCoraAccountCheck } from 'hooks/useCoraAccountCheck'
import { useCreationBlocked } from 'hooks/useCreationBlocked'
import useRouteProtection from 'hooks/useRouteProtection'
import jwt_decode from 'jwt-decode'
import {
  markMenuForGroup,
  takeGroupUnavailableNotice
} from 'lib/merchant-group'
import { FEATURE_PERMISSIONS } from 'lib/permissions/route-permissions'
import mixpanel from 'mixpanel-browser'
import { Banner } from 'models/Banner'
import { useRouter } from 'next/router'
import { parseCookies } from 'nookies'
import { MerchantProps } from 'pages/_app'

import AppBar from 'components/AppBar'
import { Drawer, Item } from 'components/Drawer'
import GroupRouteGuard from 'components/GroupRouteGuard'
import MobileBottomNav from 'components/MobileBottomNav'
import ModalNewPlansGeneric from 'components/Modal/ModalNewPlansGeneric'
import ModalNewPlansSmall from 'components/Modal/ModalNewPlansSmall'
import ModalPricing from 'components/Modal/ModalPricing'
import NPSSurveyTrigger from 'components/Modal/ModalSurveys/ModalSurveyNPS/NPSSurveyTrigger'
import NewsBanner from 'components/NewsBanner'
import PushOptInCard from 'components/NotificationCenter/components/PushOptInCard'
import PwaInstallPrompt from 'components/PwaInstallPrompt'
import SaasCheckout from 'components/SaasCheckout'
import TopBarGlobalInfo, { QuotaParams } from 'components/TopBarGlobalInfo'

import decodeBase64 from 'utils/decode-base64-to-string'
import getCurrentMonth from 'utils/get-current-month'
import {
  CAMPAIGN_MODAL_EVENT,
  CampaignModalDetail,
  PLAN_UPGRADE_REQUIRED_EVENT,
  PlanUpgradeRequiredDetail
} from 'utils/plan-upgrade-event'
import { FeatureTabs } from 'utils/tabs'
import {
  isMobileViewport,
  registerSuperProperties,
  track
} from 'utils/tracking'

import { theme } from 'styles/theme'

import { BackgroundBlur, Content, Wrapper } from './Layout.styles'

export type TabCategory =
  | 'ASSISTANT'
  | 'COMERCIAL'
  | 'CLIENTS'
  | 'CHARGES'
  | 'OPERATION'

type Feature = keyof typeof FeatureTabs

interface SubheaderItem {
  label: string
  query?: Record<string, any>
  tab?: string | number
  router?: string
  news?: boolean
  tag?: 'NOVO' | 'BETA'
  icon?: Icons
  upgrade?: boolean
  coexAlert?: boolean
}

interface MenuEntryBase {
  icon: Icons | React.ReactNode
  label: string
  router: string
  option?: string
  news?: boolean
}

interface MenuEntryWithSubheaders extends MenuEntryBase {
  subheaderItems: Partial<Record<Feature, SubheaderItem>>
}

interface MenuItem extends MenuEntryBase {
  subheader?: SubheaderItem[]
}

interface MenuItemConfig {
  entry: MenuEntryWithSubheaders
  subheaders: Feature[]
}

interface RoleMenuConfig {
  menuEntries: (MenuEntryWithSubheaders | MenuItemConfig)[]
}

const MenuEntries: Record<TabCategory, MenuEntryWithSubheaders> = {
  ASSISTANT: {
    icon: 'autoAwesome',
    label: 'Assistente',
    router: '/visao-geral',
    subheaderItems: {}
  },
  COMERCIAL: {
    icon: <FontAwesomeIcon icon={faRocket} />,
    label: 'Comercial',
    router: '/comercial',
    option: 'comercial',
    subheaderItems: {
      REGISTRATION_LINK: {
        label: 'Link de Cadastro',
        query: { tab: FeatureTabs.REGISTRATION_LINK },
        tab: FeatureTabs.REGISTRATION_LINK
      },
      PRODUCTS: {
        label: 'Planos',
        query: { tab: FeatureTabs.PRODUCTS },
        tab: FeatureTabs.PRODUCTS
      },
      CONTRACTS: {
        label: 'Contratos',
        query: { tab: FeatureTabs.CONTRACTS },
        tab: FeatureTabs.CONTRACTS
      },
      PAYMENT_LINK: {
        label: 'Link de Contratação',
        query: { tab: FeatureTabs.PAYMENT_LINK },
        tab: FeatureTabs.PAYMENT_LINK
      },
      SALES_PAGE: {
        label: 'Página de Vendas',
        query: { tab: FeatureTabs.SALES_PAGE },
        tab: FeatureTabs.SALES_PAGE,
        icon: 'faStar'
      },
      WORKFLOW: {
        label: 'Workflows',
        query: { tab: FeatureTabs.WORKFLOW },
        tab: FeatureTabs.WORKFLOW
      }
    }
  },
  CLIENTS: {
    icon: 'faShieldHeart',
    label: 'Relacionamento',
    router: '/clientes',
    option: 'clients',
    subheaderItems: {
      CUSTOMERS: {
        label: 'Clientes',
        query: { tab: FeatureTabs.CUSTOMERS },
        tab: FeatureTabs.CUSTOMERS
      },
      COMMUNICATION: {
        label: 'Comunicação',
        tab: FeatureTabs.COMMUNICATION,
        query: {
          tab: FeatureTabs.COMMUNICATION
        }
      },
      NOTIFICATIONS: {
        label: 'Notificações',
        router: '/notificacoes',
        coexAlert: true
      },
      PORTAL: {
        label: 'Portal de Clientes',
        query: { tab: FeatureTabs.PORTAL },
        tab: FeatureTabs.PORTAL
      }
    }
  },
  CHARGES: {
    icon: 'faCreditCard',
    label: 'Faturamento',
    router: '/cobrancas',
    option: 'receives',
    subheaderItems: {
      CHARGES: {
        label: 'Cobranças',
        tab: FeatureTabs.CHARGES,
        query: {
          tab: FeatureTabs.CHARGES,
          due_date_between: getCurrentMonth().join('|')
        }
      },
      INSTALLMENTS: {
        label: 'Parcelamentos',
        tab: FeatureTabs.INSTALLMENTS,
        query: { tab: FeatureTabs.INSTALLMENTS }
      },
      RECURRENCES: {
        label: 'Recorrências',
        query: { tab: FeatureTabs.RECURRENCES },
        tab: FeatureTabs.RECURRENCES
      },
      NFS: {
        label: 'Notas Fiscais',
        query: {
          tab: FeatureTabs.NFS,
          created_at_between: getCurrentMonth().join('|')
        },
        tab: FeatureTabs.NFS
      },
      NEGATIVATION: {
        label: 'Negativação',
        query: { tab: FeatureTabs.NEGATIVATION },
        tab: FeatureTabs.NEGATIVATION
      },
      TRANSFERS: {
        label: 'Transferências',
        query: {
          tab: FeatureTabs.TRANSFERS,
          transfer_date_between: getCurrentMonth().join('|')
        },
        tab: FeatureTabs.TRANSFERS
      }
    }
  },
  OPERATION: {
    icon: 'faChartLine',
    label: 'Financeiro',
    router: '/operacao',
    option: 'portal',
    news: true,
    subheaderItems: {
      ANALYSIS: {
        label: 'Métricas',
        tab: FeatureTabs.ANALYSIS,
        query: { tab: FeatureTabs.ANALYSIS }
      },
      ACCOUNTS_RECEIVABLE: {
        label: 'Contas a receber',
        query: {
          tab: FeatureTabs.ACCOUNTS_RECEIVABLE,
          due_date_between: getCurrentMonth().join('|')
        },
        tab: FeatureTabs.ACCOUNTS_RECEIVABLE,
        icon: 'faStar'
      },
      ACCOUNTS_PAYABLE: {
        label: 'Contas a pagar',
        query: {
          tab: FeatureTabs.ACCOUNTS_PAYABLE,
          due_date_between: getCurrentMonth().join('|')
        },
        tab: FeatureTabs.ACCOUNTS_PAYABLE
      },
      CASH_FLOW: {
        label: 'Fluxo de caixa',
        query: { tab: FeatureTabs.CASH_FLOW },
        tab: FeatureTabs.CASH_FLOW,
        icon: 'faStar'
      },
      BANK_RECONCILIATION: {
        label: 'Conciliação bancária',
        query: { tab: FeatureTabs.BANK_RECONCILIATION },
        tab: FeatureTabs.BANK_RECONCILIATION,
        icon: 'faStar'
      },
      DRE: {
        label: 'DRE',
        query: { tab: FeatureTabs.DRE },
        tab: FeatureTabs.DRE,
        icon: 'faStar'
      }
    }
  }
}

function createMenuItem(
  menuEntry: MenuEntryWithSubheaders,
  subheaderKeys: Feature[] = []
): MenuItem {
  const { subheaderItems, ...menuItemBase } = menuEntry

  if (!subheaderKeys.length) {
    return menuItemBase
  }

  const validSubheaderItems = subheaderKeys
    .filter((key) => subheaderItems[key as Feature] !== undefined)
    .map((key) => subheaderItems[key as Feature] as SubheaderItem)

  return {
    ...menuItemBase,
    subheader: validSubheaderItems
  }
}
export const roleMenuConfigs: Record<string, RoleMenuConfig> = {
  admin: {
    menuEntries: [
      MenuEntries.ASSISTANT,
      {
        entry: MenuEntries.COMERCIAL,
        subheaders: [
          'REGISTRATION_LINK',
          'PRODUCTS',
          'CONTRACTS',
          'PAYMENT_LINK',
          'SALES_PAGE',
          'WORKFLOW',
          'IMPORTS'
        ]
      },
      {
        entry: MenuEntries.CLIENTS,
        subheaders: [
          'CUSTOMERS',
          'COMMUNICATION',
          'NOTIFICATIONS',
          'PORTAL',
          'IMPORTS'
        ]
      },
      {
        entry: MenuEntries.CHARGES,
        subheaders: [
          'CHARGES',
          'INSTALLMENTS',
          'RECURRENCES',
          'NFS',
          'NEGATIVATION',
          'TRANSFERS',
          'IMPORTS'
        ]
      },
      {
        entry: MenuEntries.OPERATION,
        subheaders: [
          'ANALYSIS',
          'ACCOUNTS_RECEIVABLE',
          'ACCOUNTS_PAYABLE',
          'CASH_FLOW',
          'BANK_RECONCILIATION',
          'DRE',
          'IMPORTS'
        ]
      }
    ]
  },
  user: {
    menuEntries: [
      MenuEntries.ASSISTANT,
      {
        entry: MenuEntries.COMERCIAL,
        subheaders: [
          'REGISTRATION_LINK',
          'PRODUCTS',
          'CONTRACTS',
          'PAYMENT_LINK',
          'SALES_PAGE',
          'WORKFLOW',
          'IMPORTS'
        ]
      },
      {
        entry: MenuEntries.CLIENTS,
        subheaders: [
          'CUSTOMERS',
          'COMMUNICATION',
          'NOTIFICATIONS',
          'PORTAL',
          'IMPORTS'
        ]
      },
      {
        entry: MenuEntries.CHARGES,
        subheaders: [
          'CHARGES',
          'INSTALLMENTS',
          'RECURRENCES',
          'NFS',
          'NEGATIVATION',
          'TRANSFERS',
          'IMPORTS'
        ]
      },
      {
        entry: MenuEntries.OPERATION,
        subheaders: [
          'ANALYSIS',
          'ACCOUNTS_RECEIVABLE',
          'ACCOUNTS_PAYABLE',
          'CASH_FLOW',
          'BANK_RECONCILIATION',
          'DRE',
          'IMPORTS'
        ]
      }
    ]
  },
  operator: {
    menuEntries: [
      {
        entry: MenuEntries.COMERCIAL,
        subheaders: ['REGISTRATION_LINK', 'CONTRACTS', 'WORKFLOW', 'IMPORTS']
      },
      {
        entry: MenuEntries.CLIENTS,
        subheaders: [
          'CUSTOMERS',
          'COMMUNICATION',
          'NOTIFICATIONS',
          'PORTAL',
          'IMPORTS'
        ]
      }
    ]
  },
  financial_operator: {
    menuEntries: [
      {
        entry: MenuEntries.COMERCIAL,
        subheaders: [
          'REGISTRATION_LINK',
          'PRODUCTS',
          'CONTRACTS',
          'PAYMENT_LINK',
          'SALES_PAGE',
          'WORKFLOW',
          'IMPORTS'
        ]
      },
      {
        entry: MenuEntries.CLIENTS,
        subheaders: [
          'CUSTOMERS',
          'COMMUNICATION',
          'NOTIFICATIONS',
          'PORTAL',
          'IMPORTS'
        ]
      },
      {
        entry: MenuEntries.CHARGES,
        subheaders: ['CHARGES', 'INSTALLMENTS', 'RECURRENCES', 'NFS', 'IMPORTS']
      },
      {
        entry: MenuEntries.OPERATION,
        subheaders: [
          'ANALYSIS',
          'ACCOUNTS_RECEIVABLE',
          'ACCOUNTS_PAYABLE',
          'CASH_FLOW',
          'BANK_RECONCILIATION',
          'DRE',
          'IMPORTS'
        ]
      }
    ]
  },
  billing_operator: {
    menuEntries: [
      {
        entry: MenuEntries.COMERCIAL,
        subheaders: [
          'REGISTRATION_LINK',
          'PRODUCTS',
          'CONTRACTS',
          'PAYMENT_LINK',
          'SALES_PAGE',
          'IMPORTS'
        ]
      },
      {
        entry: MenuEntries.CLIENTS,
        subheaders: [
          'CUSTOMERS',
          'COMMUNICATION',
          'NOTIFICATIONS',
          'PORTAL',
          'IMPORTS'
        ]
      },
      {
        entry: MenuEntries.CHARGES,
        subheaders: ['CHARGES', 'INSTALLMENTS', 'RECURRENCES', 'NFS', 'IMPORTS']
      }
    ]
  },
  guest: {
    menuEntries: [
      {
        entry: MenuEntries.COMERCIAL,
        subheaders: [
          'REGISTRATION_LINK',
          'PRODUCTS',
          'CONTRACTS',
          'PAYMENT_LINK'
        ]
      },
      {
        entry: MenuEntries.CLIENTS,
        subheaders: ['CUSTOMERS', 'COMMUNICATION', 'NOTIFICATIONS', 'PORTAL']
      },
      {
        entry: MenuEntries.CHARGES,
        subheaders: ['CHARGES', 'INSTALLMENTS', 'RECURRENCES', 'NFS']
      }
    ]
  }
}

function generateMenuItems(): Record<string, MenuItem[]> {
  const menuItems: Record<string, MenuItem[]> = {}

  for (const [role, config] of Object.entries(roleMenuConfigs)) {
    menuItems[role] = config.menuEntries.map((item) => {
      if ('entry' in item) {
        return createMenuItem(item.entry, item.subheaders)
      }
      return createMenuItem(item as MenuEntryWithSubheaders)
    })
  }

  menuItems.user = menuItems.admin

  return menuItems
}

export const menuItems = generateMenuItems()

function generateTabs(
  userRole?: string
): Record<
  Exclude<TabCategory, 'ASSISTANT'>,
  { label: string; value: string | number }[]
> {
  const tabs: Record<
    Exclude<TabCategory, 'ASSISTANT'>,
    { label: string; value: string | number }[]
  > = {
    COMERCIAL: [],
    CLIENTS: [],
    CHARGES: [],
    OPERATION: []
  }

  const role = userRole || decodeBase64(parseCookies()['nextauth.role'] || '')

  const config = roleMenuConfigs[role || 'guest']
  if (!config) return tabs

  for (const item of config.menuEntries) {
    if ('entry' in item) {
      let category: Exclude<TabCategory, 'ASSISTANT'> | undefined
      for (const key in MenuEntries) {
        if (MenuEntries[key as TabCategory] === item.entry) {
          category = key as Exclude<TabCategory, 'ASSISTANT'>
          break
        }
      }

      if (
        category &&
        Object.prototype.hasOwnProperty.call(MenuEntries, category)
      ) {
        const menuCategory = MenuEntries[
          category
        ] as (typeof MenuEntries)[keyof typeof MenuEntries]

        tabs[category] = item.subheaders
          .filter((subheader) => {
            const sh = menuCategory.subheaderItems[subheader as Feature]
            return sh !== undefined && !sh.router
          })
          .map((subheader) => {
            const subheaderItem =
              menuCategory.subheaderItems[subheader as Feature]
            return {
              label: subheaderItem?.label || '',
              value: subheaderItem?.tab || ''
            }
          })
      }
    }
  }

  return tabs
}

export const roleTabs = generateTabs()

export { generateTabs }

/**
 * Regra única de visibilidade de um item de seção (menu lateral e abas).
 * Item sem permissão mapeada aparece; WORKFLOW sem acesso do merchant aparece
 * (com o badge de upgrade).
 */
export const isFeatureAllowed = (
  feature: Feature,
  hasPermission: (permission: string) => boolean,
  capabilities?: { workflow?: { has_access?: boolean } } | null
): boolean => {
  if (feature === 'WORKFLOW') {
    if (!capabilities?.workflow?.has_access) return true
    return hasPermission('workflows:show')
  }

  const perm = FEATURE_PERMISSIONS[feature]
  if (!perm) {
    if (feature === 'IMPORTS') return hasPermission('imports:show')
    return true
  }
  return hasPermission(perm)
}

/**
 * Abas de cada seção pela permissão do usuário, na mesma regra do menu lateral.
 * O generateTabs (por papel) deixava a barra vazia para o Operador em
 * Faturamento e mostrava abas que o perfil não abre.
 */
export function generatePermissionTabs(
  hasPermission: (permission: string) => boolean,
  capabilities?: { workflow?: { has_access?: boolean } } | null
): Record<
  Exclude<TabCategory, 'ASSISTANT'>,
  { label: string; value: string | number }[]
> {
  const tabs: Record<
    Exclude<TabCategory, 'ASSISTANT'>,
    { label: string; value: string | number }[]
  > = {
    COMERCIAL: [],
    CLIENTS: [],
    CHARGES: [],
    OPERATION: []
  }

  for (const category of Object.keys(tabs) as Exclude<
    TabCategory,
    'ASSISTANT'
  >[]) {
    const subheaderItems = MenuEntries[category].subheaderItems
    tabs[category] = (Object.keys(subheaderItems) as Feature[])
      .filter((feature) => {
        const sh = subheaderItems[feature]
        return (
          sh !== undefined &&
          !sh.router &&
          isFeatureAllowed(feature, hasPermission, capabilities)
        )
      })
      .map((feature) => ({
        label: subheaderItems[feature]?.label || '',
        value: subheaderItems[feature]?.tab || ''
      }))
  }

  return tabs
}

function generateTabRoutes(): Record<Feature, string> {
  const routes: Record<string, string> = {}
  function queryParamsToString(params: Record<string, any>): string {
    const queryParams = Object.entries(params)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&')
    return queryParams ? `?${queryParams}` : ''
  }
  for (const categoryKey in MenuEntries) {
    const category = MenuEntries[categoryKey as TabCategory]
    const baseRouter = category.router
    for (const [featureKey, subheaderItem] of Object.entries(
      category.subheaderItems
    )) {
      if (subheaderItem.router) {
        routes[featureKey] = subheaderItem.router
      } else if (subheaderItem.query) {
        const queryParams = { ...subheaderItem.query }
        const queryString = queryParamsToString(queryParams)
        routes[featureKey] = `${baseRouter}${queryString}`
      } else {
        routes[featureKey] = baseRouter
      }
    }
  }
  return routes
}
export const tabRoutes = generateTabRoutes()

export type Components = 'drawer' | 'appbar' | 'footer'

type LayoutProps = {
  children: JSX.Element
}

function Layout({ children }: LayoutProps) {
  const { user, modalPricing, setModalPricing, capabilities, groupSession } =
    useAuthContext()
  const { creationBlocked } = useCreationBlocked()
  const [campaignModal, setCampaignModal] =
    useState<CampaignModalDetail | null>(null)

  // Modais da campanha de fim do Free, abertos por qualquer tentativa de criar
  // algo com a conta limitada (SC-10363). Ficam aqui, e não na Visão Geral,
  // porque o bloqueio acontece em qualquer tela.
  useEffect(() => {
    const handleCampaignModal = (event: Event) => {
      const detail = (event as CustomEvent<CampaignModalDetail>).detail
      if (detail?.kind) setCampaignModal(detail)
    }

    window.addEventListener(CAMPAIGN_MODAL_EVENT, handleCampaignModal)
    return () =>
      window.removeEventListener(CAMPAIGN_MODAL_EVENT, handleCampaignModal)
  }, [])
  const { pathname } = useRouter()
  const { hasPermission, permissions } = usePermissionsContext()

  useRouteProtection()

  const { ['nextauth.token']: token, ['nextauth.status']: status } =
    parseCookies()
  const [quota, setQuota] = useState<QuotaParams>()
  const [quotaLoaded, setQuotaLoaded] = useState(false)
  const [drawerIsOpen, setDrawerIsOpen] = useState(false)
  const [loading, setLoading] = useState(true)

  const [banner, setBanner] = useState<Banner | null>(null)
  const [isBannerOpen, setIsBannerOpen] = useState<boolean>(true)

  const [isExpanded, setIsExpanded] = useState(true)
  const [isExpandedInput, setIsExpandedInput] = useState(false)

  const match = useMediaQuery('(max-width: 770px)', { noSsr: false })

  const handleToggleIsExpanded = () => {
    setIsExpanded(!isExpanded)
    localStorage.setItem('expanded-drawer', String(!isExpanded))
  }

  const expandDrawer = () => {
    setIsExpanded(true)
    localStorage.setItem('expanded-drawer', String(true))
  }

  useEffect(() => {
    // SC-10999: quota e ativação são da empresa; na visão do grupo seriam só as
    // da empresa de contexto
    if (!token || groupSession) return

    const fetchQuota = async () => {
      try {
        const response = await DataService({
          type: 'GET',
          url: `/v1/onboarding/get_status_activation`
        })
        setQuota({
          stage: response.data.record.stage,
          free_tier: response.data.record.free_tier,
          expiresIn: response.data.record.free_tier_end_date,
          billing: {
            max: response.data.record.free_tier_billing_quota,
            actual:
              response.data.record.free_tier_billing_quota -
              response.data.record.free_tier_billing_current_quota
          },
          nfse: {
            max: response.data.record.free_tier_nfse_quota,
            actual:
              response.data.record.free_tier_nfse_quota -
              response.data.record.free_tier_nfse_current_quota
          }
        })
        setQuotaLoaded(true)
      } catch {
        // silently fail - quota data is not critical
      }
    }
    fetchQuota()
  }, [token, groupSession])

  useEffect(() => {
    if (!match) {
      setIsExpandedInput(false)
    }
  }, [match])

  // SC-10999: o grupo deixou de valer e o app voltou para a empresa de
  // contexto (ver leaveUnavailableGroup); explica o que aconteceu uma vez
  useEffect(() => {
    const notice = takeGroupUnavailableNotice()
    if (!notice) return
    // Espera o ToastContainer montar, senão o aviso some no primeiro render
    const timer = setTimeout(() => toast.info(notice), 1200)
    return () => clearTimeout(timer)
  }, [])

  // O Drawer lê `expanded-drawer` direto do localStorage, então sem esse sync o
  // Layout achava que o menu estava expandido em todo reload — o que desalinha
  // a AppBar e o `--cb-drawer-width`. Só ajusta quando a chave já existe, para
  // não mudar o default de quem nunca recolheu o menu.
  useEffect(() => {
    const stored = localStorage.getItem('expanded-drawer')
    if (stored !== null) {
      setIsExpanded(stored === 'true')
    }
  }, [])

  // Rede de segurança do bloqueio por plano (SC-10363): quando a API recusa uma
  // criação com `plan_upgrade_required`, o DataService emite este evento e o
  // modal de upgrade abre — mesmo que a ação não passe pelo CreateGate.
  useEffect(() => {
    const handlePlanUpgradeRequired = (event: Event) => {
      const { resourceName, plan } =
        (event as CustomEvent<PlanUpgradeRequiredDetail>).detail || {}

      setModalPricing({
        isOpen: true,
        plan: plan || 'start',
        resourceName: resourceName || 'Criação de novos itens'
      })
    }

    window.addEventListener(
      PLAN_UPGRADE_REQUIRED_EVENT,
      handlePlanUpgradeRequired
    )

    return () =>
      window.removeEventListener(
        PLAN_UPGRADE_REQUIRED_EVENT,
        handlePlanUpgradeRequired
      )
  }, [setModalPricing])
  // SC-10692: o init saiu daqui para o _app.tsx (as páginas deslogadas também
  // precisam do Mixpanel). Antes, init e leitura de `__loaded` aconteciam no
  // mesmo tick: se a lib ainda não tivesse terminado de carregar, o identify
  // era descartado sem aviso. Aqui só identificamos, e sem o guard de
  // `__loaded` — a fila interna do mixpanel-browser já segura as chamadas
  // feitas antes do carregamento.
  // SC-10999: na visão multi-empresas a sessão é do grupo, e o Mixpanel segue
  // identificando por empresa: a principal do grupo.
  const mixpanelIdentity = groupSession?.main_merchant_uuid || user?.uuid

  useEffect(() => {
    if (!user?.uuid || !mixpanelIdentity) return

    try {
      mixpanel.identify(mixpanelIdentity)
      mixpanel.people.set({
        $email: user.email,
        $name: user.name,
        plan: user.current_plan,
        role: user.role,
        merchant_stage: user.stage
      })
    } catch {
      // Telemetria nunca pode quebrar o carregamento do app.
    }

    // SC-10694: super properties. Nenhum `mixpanel.register()` existia no
    // projeto, então nenhum evento carregava plano ou papel do usuário e nada
    // era segmentável. É o corte que responde se a queda de ativação (17,3%
    // para 7,5% entre junho e agosto) é qualidade de mídia ou produto.
    registerSuperProperties({
      merchant_uuid: mixpanelIdentity,
      plan: user.current_plan,
      role: user.role,
      merchant_stage: user.stage,
      acquisition_source: user.acquisition_source,
      acquisition_medium: user.acquisition_medium,
      acquisition_campaign: user.acquisition_campaign
    })

    // SC-11130: sem isso o erro chega no Sentry sem dizer de qual conta veio,
    // e o suporte não consegue cruzar com o chamado. Só ids, nada de PII.
    try {
      Sentry.setUser({ id: user.uuid })
      Sentry.setTags({
        merchant_uuid: mixpanelIdentity,
        plan: user.current_plan,
        role: user.role
      })
    } catch {
      // Telemetria nunca pode quebrar o carregamento do app.
    }
  }, [
    user?.uuid,
    mixpanelIdentity,
    user?.email,
    user?.name,
    user?.current_plan,
    user?.role,
    user?.stage,
    user?.acquisition_source,
    user?.acquisition_medium,
    user?.acquisition_campaign
  ])

  // SC-10694: primeira sessão de cada conta. Das 159 contas criadas em agosto,
  // 84 pararam na primeira tela e 94 de 96 nunca fizeram um segundo login —
  // então é essa sessão, e só ela, que explica o abandono. A marca fica no
  // localStorage por dispositivo: serve para separar "primeira vez que abriu o
  // app" do retorno, sem depender de chamada extra à API.
  useEffect(() => {
    if (!user?.uuid) return

    try {
      const key = `cb:first-login:${user.uuid}`
      if (!localStorage.getItem(key)) {
        // SC-10944: o canal vai no payload, e não só na super property. O
        // register acontece no efeito acima, mas basta o `user` chegar sem as
        // `acquisition_*` (o login preenchia o estado em memória sem elas) para
        // o registro ser filtrado e este evento — que dispara uma única vez por
        // conta — sair sem origem, sem chance de reemissão.
        track('first_login', {
          merchant_stage: user.stage,
          plan: user.current_plan,
          is_mobile: isMobileViewport(),
          ...(!!user.acquisition_source && {
            acquisition_source: user.acquisition_source
          }),
          ...(!!user.acquisition_medium && {
            acquisition_medium: user.acquisition_medium
          }),
          ...(!!user.acquisition_campaign && {
            acquisition_campaign: user.acquisition_campaign
          })
        })
        // Só marca depois de enviar: se o track falhar, a próxima montagem
        // tenta de novo em vez de perder o evento em definitivo.
        localStorage.setItem(key, new Date().toISOString())
      }
    } catch {
      // localStorage indisponível (aba privada): só perdemos a marcação.
    }
  }, [
    user?.uuid,
    user?.stage,
    user?.current_plan,
    user?.acquisition_source,
    user?.acquisition_medium,
    user?.acquisition_campaign
  ])

  // Page view por rota, restrito às contas em onboarding (`pending`).
  //
  // Deliberadamente NÃO enviamos page view de todo mundo: a área logada gera
  // 1.088.079 page views/mês (medido no GA4), contra 80.307 eventos/mês que o
  // Mixpanel recebe hoje no total — seria multiplicar o volume por ~14 para
  // duplicar um dado que o GA4 já coleta melhor e sem limite de cota.
  //
  // O que o GA4 não responde é a pergunta deste card: por onde andaram as 84
  // contas/mês que param na primeira tela e não voltam. Para isso basta o
  // recorte de quem ainda está em onboarding, que é um volume pequeno e o
  // único com `merchant_uuid` e plano nas super properties para cruzar.
  // Limitado às 24h após o primeiro login: é a janela em que o abandono
  // acontece (94 de 96 nunca fazem um segundo login) e mantém o volume baixo,
  // em vez de acompanhar para sempre os ~6.000 merchants que estão em
  // `pending` há meses e não voltam.
  useEffect(() => {
    if (!user?.uuid || !pathname) return
    if (user.stage !== 'pending') return

    try {
      const firstLoginAt = localStorage.getItem(`cb:first-login:${user.uuid}`)
      if (!firstLoginAt) return

      const hoursSince =
        (Date.now() - new Date(firstLoginAt).getTime()) / 3_600_000
      if (hoursSince > 24) return
    } catch {
      return
    }

    track('page_viewed', {
      path: pathname,
      merchant_stage: user.stage,
      is_mobile: isMobileViewport()
    })
  }, [pathname, user?.uuid, user?.stage])

  useEffect(() => {
    const isExpanded = localStorage.getItem('expanded-drawer') === 'true'
    setIsExpanded(isExpanded)

    const bannerString = localStorage.getItem('banner')
    const banners = bannerString
      ? (JSON.parse(bannerString) as Banner[] | Banner)
      : null
    setBanner(Array.isArray(banners) ? banners[0] : banners)
  }, [children])

  const getMerchant = useCallback((token: string) => {
    setLoading(true)

    try {
      const merchant = jwt_decode(token) as MerchantProps
      if (!merchant) return
    } catch (err) {
      throw new Error('Merchant is not identified')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    token ? getMerchant(token) : setLoading(false)
  }, [token])

  // Verifica se a conta Cora está conectada (apenas para plano basic_cora)
  useCoraAccountCheck()

  // Gera menu baseado em permissoes granulares
  const permissionBasedMenu = useMemo((): MenuItem[] => {
    // Se nao tem permissoes no contexto, retorna vazio para usar fallback
    if (permissions.length === 0) return []

    const items: MenuItem[] = []

    for (const [category, menuEntry] of Object.entries(MenuEntries)) {
      // Assistente: verificacao simples com dashboard:view
      if (category === 'ASSISTANT') {
        if (hasPermission('dashboard:view')) {
          items.push(createMenuItem(menuEntry))
        }
        continue
      }

      // Outras secoes: filtrar sub-items por permissao
      const subheaderKeys = Object.keys(menuEntry.subheaderItems) as Feature[]
      const visibleSubheaders = subheaderKeys.filter((feature) =>
        isFeatureAllowed(feature, hasPermission, capabilities)
      )

      // So adiciona secao se tiver sub-items visiveis
      if (visibleSubheaders.length > 0) {
        items.push(createMenuItem(menuEntry, visibleSubheaders))
      }
    }

    return items
  }, [permissions, hasPermission, capabilities])

  if (loading) {
    return <Loader />
  }

  const routesHiddenDrawer = [
    '/clientes/ficha-cadastral/[uuid]',
    '/transfers/[uuid]/report',
    '/forgot-password/[id]',
    '/forgot-password',
    '/login',
    '/login/ativar-conta',
    '/registrar',
    '/onboarding/upload/[merchantUuid]/[token]'
  ]

  const routesHiddenAppBar = [
    '/clientes/ficha-cadastral/[uuid]',
    '/transfers/[uuid]/report',
    '/forgot-password/[id]',
    '/forgot-password',
    '/login',
    '/login/ativar-conta',
    '/registrar',
    '/onboarding/upload/[merchantUuid]/[token]'
  ]

  const DefaultMenuItems = (): Item[] => {
    // Se menu baseado em permissoes disponivel, usar
    if (permissionBasedMenu.length > 0) {
      return permissionBasedMenu
    }

    // Fallback para sistema de roles (retrocompatibilidade)
    if (!['operator', 'financial_operator'].includes(user?.role || 'guest')) {
      return menuItems[user?.role || 'guest'].map((item) => {
        return item
      })
    }
    return menuItems[user?.role || 'guest']
  }
  // SC-10999: na visão multi-empresas as telas por empresa continuam no menu,
  // marcadas como indisponíveis (ver markMenuForGroup)
  const MenuItems = (): Item[] =>
    groupSession
      ? markMenuForGroup(DefaultMenuItems() as MenuItem[])
      : DefaultMenuItems()

  /*
  const showCarousel =
    banners != null &&
    ((pathname == '/visao-geral' && banners.length) ||
      (pathname != '/visao-geral' &&
        banners.filter((b) => b.showOnAllPages).length > 0))
  */

  const showBanner =
    banner &&
    isBannerOpen &&
    (pathname === '/visao-geral' || banner.showOnAllPages)

  // Verifica se deve exibir aviso da Cora (plano basic_cora sem conta vinculada)
  const showCoraWarning =
    (user?.current_plan === 'basic_cora' || user?.cora_enabled) &&
    !user?.cora_bank

  const quotaLimitReached =
    quota?.billing.actual == quota?.billing.max &&
    quota?.nfse.actual == quota?.nfse.max

  const isTrial = user?.current_plan === 'plan_trial'

  /**
   * Ativação pendente (SC-10879).
   *
   * O CTA "Concluir cadastro" mora dentro da top bar, e até aqui a barra só
   * subia para quem estava em `plan_trial`. Quem recebia um plano pago antes de
   * terminar o onboarding — comercial fechando a venda durante o cadastro —
   * caía fora de todas as condições e ficava sem nenhum caminho para ativar a
   * conta: sem KYC não recebe por cartão nem saca, e o único CTA restante era o
   * aviso de cartão de crédito, que só aparece se ele tentar criar uma cobrança
   * com cartão.
   *
   * O sinal certo é o estado da conta, não o plano contratado: `nextauth.status`
   * carrega `merchants.status`, que segue `trial` até a ativação sair. É o mesmo
   * dado que o botão dentro da TopBarGlobalInfo já consulta — o furo estava só
   * nesta porta, que decidia pelo plano.
   */
  const activationPending = user?.status === 'trial'

  const isExcludedRoute =
    pathname === '/login' ||
    pathname === '/registrar' ||
    pathname === '/cadastro' ||
    pathname === '/transfers'

  // Exibe a top bar azul para conta limitada, aviso da Cora, ativação
  // pendente, trial ou free_tier com quota disponível.
  const showTopBarGlobalInfo =
    // Conta limitada (SC-10363): a barra é permanente e não depende de quota.
    (creationBlocked ||
      showCoraWarning ||
      // Ativação pendente (SC-10879): vale para qualquer plano, inclusive pago.
      activationPending ||
      // `isTrial` sozinho, sem olhar `quota`: a quota chega por API e amarrar a
      // barra a ela fazia a top bar piscar em conta trial (SC-9544). Cobre o que
      // o antigo `isFreeTierActive` (free_tier && isTrial) já dizia.
      isTrial ||
      (quota?.free_tier && !quotaLimitReached)) &&
    !isExcludedRoute &&
    // SC-10999: conta limitada, Cora, ativação e trial são avisos de uma
    // empresa; em "Todas as empresas" mostrariam só a de contexto (com CTA de
    // concluir o cadastro dela)
    !groupSession

  return (
    <Wrapper>
      <NPSSurveyTrigger />
      {showTopBarGlobalInfo && (
        <TopBarGlobalInfo
          quota={quota}
          showCoraWarning={showCoraWarning}
          quotaLoaded={quotaLoaded}
        />
      )}

      {!routesHiddenDrawer.includes(pathname) && (
        <Drawer
          showTopGlobalInfo={showTopBarGlobalInfo}
          drawerIsOpen={drawerIsOpen}
          menuItems={MenuItems()}
          setDrawerIsOpen={setDrawerIsOpen}
          isExpanded={isExpanded}
          handleToggleIsExpanded={handleToggleIsExpanded}
          expandDrawer={expandDrawer}
        />
      )}
      {!routesHiddenAppBar.includes(pathname) && (
        <AppBar
          showTopGlobalInfo={showTopBarGlobalInfo}
          setDrawerIsOpen={setDrawerIsOpen}
          isExpandedDrawer={isExpanded}
          setIsExpandedMobileMenu={setIsExpandedInput}
          isExpandedMobileMenu={isExpandedInput}
        />
      )}
      <Content
        sx={{
          // Largura atual do menu lateral, publicada para elementos `fixed`
          // dentro do conteúdo (ex.: BatchActionsBar) se alinharem à área útil
          // sem hardcodar a largura do Drawer. Abaixo de 770px o menu é
          // temporário (não ocupa espaço), então vale 0.
          '--cb-drawer-width': '0px',
          [theme.breakpoints.up(770)]: {
            '--cb-drawer-width': routesHiddenDrawer.includes(pathname)
              ? '0px'
              : isExpanded
              ? '240px'
              : '100px'
          },
          ...(showTopBarGlobalInfo && {
            pt: '50px',
            [theme.breakpoints.down(450)]: {
              pt: '90px'
            }
          }),
          borderLeft: `1px solid ${theme.palette.neutral['700']}`
        }}
        isExpandedInput={isExpandedInput}
        appbarIsNotHidded={!routesHiddenAppBar.includes(pathname)}
        showActivateAccountButton={status === 'trial' && !quota?.free_tier}
      >
        {/*banners && (
          <Box padding="20px 20px 0 20px">
            {showCarousel && (
              <Carousel sx={{ overflow: 'visible' }} interval={4000}>
                {banners
                  .filter(
                    (b) => pathname === '/visao-geral' || b.showOnAllPages
                  )
                  .map((banner) => (
                    <NewsBanner banner={banner} key={banner.link} />
                  ))}
              </Carousel>
            )}
          </Box>
        )*/}
        {showBanner && !routesHiddenAppBar.includes(pathname) && (
          <Box
            padding="20px 20px 0px 20px"
            bgcolor={pathname === '/visao-geral' ? '#f4f8f7' : '#fff'}
          >
            <NewsBanner banner={banner} setIsBannerOpen={setIsBannerOpen} />
          </Box>
        )}
        {!routesHiddenAppBar.includes(pathname) && <AccountAlertBanner />}
        {!routesHiddenAppBar.includes(pathname) && <PwaInstallPrompt />}
        {!routesHiddenAppBar.includes(pathname) && <PushOptInCard />}
        {isExpandedInput && <BackgroundBlur role="presentation" />}
        <GroupRouteGuard>{children}</GroupRouteGuard>
      </Content>

      {modalPricing?.isOpen && (
        <ModalPricing
          {...modalPricing}
          handleCloseModal={() =>
            setModalPricing((prev) => ({ ...prev, isOpen: false }))
          }
        />
      )}

      {/* Contratação/upgrade do plano dentro do CONTA (SC-11070) */}
      <SaasCheckout />

      <ModalNewPlansSmall
        open={campaignModal?.kind === 'small'}
        onClose={() => setCampaignModal(null)}
      />
      <ModalNewPlansGeneric
        open={
          campaignModal?.kind === 'generic' ||
          campaignModal?.kind === 'trial_ended'
        }
        variant={
          campaignModal?.kind === 'trial_ended' ? 'trial_ended' : 'free_ended'
        }
        onClose={() => setCampaignModal(null)}
      />

      {!routesHiddenAppBar.includes(pathname) && <MobileBottomNav />}
    </Wrapper>
  )
}

export default memo(Layout)
